// Public, no-login feedback and complaints form at /submit-feedback — reached from
// our website, from the QR code on the Feedback page, or opened by a member of staff
// writing down what a participant or member of the public has told them.
//
// Rendered directly by index.js, OUTSIDE the auth gate, MSAL and the app providers,
// like the kit list: it never triggers a sign-in, and it fetches with plain `fetch`
// rather than the app's axios (whose interceptors would attach a token nobody here
// has, and raise toasts into a page with no Notifier).
//
// Spam is held off server-side (backend core/feedback.py). This page's part is the
// Cloudflare Turnstile widget, when the server says one is configured, and a honeypot
// field people never see.
import { useCallback, useEffect, useRef, useState } from "react";
import {
  Alert, AlertTitle, Box, Button, CircularProgress, Divider, Stack, Typography,
} from "@mui/material";
import { API_BASE_URL } from "../config";
import FeedbackForm from "../components/FeedbackForm";
import { emptyFeedbackForm, publicFeedbackBody, submissionProblems } from "../feedbackRules";

const LOGO_URL = process.env.PUBLIC_URL + "/ShelterStoneLogo.png";
const API = `${API_BASE_URL}/public-feedback`;
const TURNSTILE_SRC = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";

// Load the Turnstile script once, however many times the widget is drawn.
let turnstileLoading = null;
const loadTurnstile = () => {
  if (window.turnstile) return Promise.resolve(window.turnstile);
  if (!turnstileLoading) {
    turnstileLoading = new Promise((resolve, reject) => {
      const s = document.createElement("script");
      s.src = TURNSTILE_SRC;
      s.async = true;
      s.onload = () => resolve(window.turnstile);
      s.onerror = () => { turnstileLoading = null; reject(new Error("captcha")); };
      document.head.appendChild(s);
    });
  }
  return turnstileLoading;
};

// The server's error as a sentence, whatever shape it came back in.
const errorText = async (res) => {
  try {
    const body = await res.json();
    if (typeof body.detail === "string") return body.detail;
  } catch { /* not JSON */ }
  return "Something went wrong sending your feedback. Please try again.";
};

function Captcha({ siteKey, onToken, resetRef }) {
  const box = useRef(null);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    let widget = null;
    let live = true;
    loadTurnstile().then(ts => {
      if (!live || !box.current) return;
      widget = ts.render(box.current, {
        sitekey: siteKey,
        callback: (t) => onToken(t),
        "expired-callback": () => onToken(null),
        "error-callback": () => onToken(null),
      });
      // A token is single-use, so a rejected submission needs a fresh one.
      resetRef.current = () => { onToken(null); ts.reset(widget); };
    }).catch(() => live && setFailed(true));
    return () => {
      live = false;
      if (widget != null && window.turnstile) window.turnstile.remove(widget);
    };
  }, [siteKey, onToken, resetRef]);
  if (failed) {
    return (
      <Alert severity="warning">
        The check that you're not a robot couldn't load. Please check your connection and reload the page.
      </Alert>
    );
  }
  return <Box ref={box} sx={{ minHeight: 65 }} />;
}

export default function SubmitFeedback() {
  // loading | ready | error | done
  const [phase, setPhase] = useState("loading");
  const [options, setOptions] = useState({ events: [], captcha_site_key: null });
  const [form, setForm] = useState(emptyFeedbackForm);
  const [honeypot, setHoneypot] = useState("");
  const [token, setToken] = useState(null);
  const [showErrors, setShowErrors] = useState(false);
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState(null);
  const [receipt, setReceipt] = useState(null);
  const resetCaptcha = useRef(() => {});
  const onToken = useCallback((t) => setToken(t), []);

  useEffect(() => {
    document.title = "Feedback — Shelter Stone";
    let live = true;
    fetch(`${API}/options`)
      .then(res => { if (!res.ok) throw new Error(`HTTP ${res.status}`); return res.json(); })
      .then(data => { if (live) { setOptions(data); setPhase("ready"); } })
      .catch(() => live && setPhase("error"));
    return () => { live = false; };
  }, []);

  const problems = submissionProblems(form);
  const needsCaptcha = !!options.captcha_site_key;

  const submit = async () => {
    setShowErrors(true);
    setSendError(null);
    if (problems.length || sending) return;
    if (needsCaptcha && !token) {
      setSendError("Please complete the check that you're not a robot.");
      return;
    }
    setSending(true);
    try {
      const res = await fetch(`${API}/`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...publicFeedbackBody(form), captcha_token: token, website: honeypot }),
      });
      if (!res.ok) {
        setSendError(await errorText(res));
        resetCaptcha.current();
        return;
      }
      setReceipt(await res.json());
      setPhase("done");
      window.scrollTo(0, 0);
    } catch {
      setSendError("We couldn't reach the server. Please check your connection and try again.");
      resetCaptcha.current();
    } finally {
      setSending(false);
    }
  };

  const startAgain = () => {
    setForm(emptyFeedbackForm());
    setShowErrors(false);
    setSendError(null);
    setReceipt(null);
    setToken(null);
    setPhase("ready");
  };

  return (
    <Box sx={{ maxWidth: 700, mx: "auto", p: 2, pb: 6 }}>
      <Box sx={{ textAlign: "center", mb: 2 }}>
        <img src={LOGO_URL} alt="Shelter Stone" style={{ maxWidth: 260, width: "100%" }} />
      </Box>

      {phase === "loading" && (
        <Stack sx={{ alignItems: "center", py: 6 }} spacing={2}>
          <CircularProgress />
        </Stack>
      )}

      {phase === "error" && (
        <Alert severity="error">
          <AlertTitle>The form couldn't load</AlertTitle>
          Check your connection and try again — or email us at{" "}
          <a href="mailto:hello@shelter-stone.com">hello@shelter-stone.com</a>.
        </Alert>
      )}

      {phase === "done" && (
        <Alert severity="success">
          <AlertTitle>Thank you for your feedback</AlertTitle>
          <Typography variant="body2" gutterBottom>
            We've received it{receipt?.reference ? <> — your reference is <strong>{receipt.reference}</strong></> : ""}.
            Every piece of feedback is read by our team, and it helps us look after people better.
          </Typography>
          {receipt?.acknowledged && (
            <Typography variant="body2" gutterBottom>We've emailed you to confirm.</Typography>
          )}
          <Button size="small" onClick={startAgain} sx={{ mt: 1 }}>Give more feedback</Button>
        </Alert>
      )}

      {phase === "ready" && (
        <>
          <Typography variant="h4" gutterBottom>Feedback and complaints</Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            Whether it's praise, a suggestion or a complaint, we'd like to hear it. Only your
            feedback itself is required, but the more you can tell us the more we can do with it.
          </Typography>

          <FeedbackForm form={form} setForm={setForm} events={options.events}
                        showErrors={showErrors} />

          {/* The honeypot: off-screen and out of the tab order, so a person never fills
              it in and a form-filling bot usually does. */}
          <Box aria-hidden="true" sx={{ position: "absolute", left: "-10000px", width: 1, height: 1,
                                        overflow: "hidden" }}>
            <label>Website
              <input type="text" name="website" tabIndex={-1} autoComplete="off"
                     value={honeypot} onChange={(e) => setHoneypot(e.target.value)} />
            </label>
          </Box>

          <Divider sx={{ my: 3 }} />
          <Stack spacing={2}>
            {needsCaptcha && (
              <Captcha siteKey={options.captcha_site_key} onToken={onToken} resetRef={resetCaptcha} />
            )}
            {showErrors && problems.length > 0 && (
              <Alert severity="warning">{problems.join(" ")}</Alert>
            )}
            {sendError && <Alert severity="error">{sendError}</Alert>}
            <Box>
              <Button variant="contained" size="large" onClick={submit} disabled={sending}>
                {sending ? "Sending…" : "Send feedback"}
              </Button>
            </Box>
            <Typography variant="caption" color="text.secondary">
              We keep what you send us, and the address it was sent from, so we can deal with it
              properly. You can also email us at{" "}
              <a href="mailto:hello@shelter-stone.com">hello@shelter-stone.com</a>.
            </Typography>
          </Stack>
        </>
      )}
    </Box>
  );
}
